import { Icon } from "@material-ui/core";
import React, { useMemo, useState } from "react";

import { ActionPermissionsObject, PermissionActionType, WorkspaceResourceFolderType, WorkspaceResourceType } from "common/interfaces";
import PresentationTemplatesPaneController, {
    PresentationTemplatesPaneControllerState,
} from "js/controllers/PresentationTemplatesPaneController";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import { trackActivity } from "js/core/utilities/utilities";
import { CreatePresentationDialogPaneType } from "js/editor/CreatePresentation/CreatePresentationDialogTypes";
import { PresentationListHeader } from "js/editor/PresentationLibrary/PresentationListHeader";
import { SearchAndAdd } from "js/editor/PresentationLibrary/components/SearchAndAdd";
import {
    buildResourcePaneBreadcrumbData,
    PresentationLibraryPaneBreadcrumb,
} from "js/editor/PresentationLibrary/components/PresentationLibraryPaneBreadcrumb";
import { ManageTemplatesLink } from "js/editor/PresentationLibrary/ManageTemplatesLink";
import {
    contentFilterOptions,
    SEARCH_FIELD_ALL,
    sortOptions,
    TAG_FILTER_ALL,
    TemplatesContent,
    TemplatesListHeaderActions,
    TemplatesListHeaderRow,
    UpgradeIconWrapper,
} from "js/editor/PresentationLibrary/libraryTemplatesPaneShared";
import { t } from "js/i18n";
import { FlexBox } from "js/react/components/LayoutGrid";
import { Layout, PaneBody, PaneContent, SearchBarSort } from "js/react/views/SharedResources/Components";
import PresentationTemplatesPane from "js/react/views/SharedResources/PresentationTemplatesPane";
import {
    selectedClass,
    SideBarItem,
} from "js/react/views/SharedResources/Components/WorkspaceResourcesSidebarHelper";
import SideBar from "js/react/views/SharedResources/Components/SideBar";
import { ShowUpgradeDialog } from "js/react/components/Dialogs/BaseDialog";
import { UpgradePlanDialogType } from "js/react/views/MarketingDialogs/UpgradePlanDialog";

type TeamTemplatesPaneContentProps = PresentationTemplatesPaneControllerState & WorkspaceControllerState & {
    onTemplateChange?: (view: "starter" | "team") => void;
    onCreate: (options: { type: "team-template"; templateId: string }) => void | Promise<void>;
    showManageTemplatesLink?: boolean;
};

function TeamTemplatesPaneContent({
    tags,
    search,
    sort,
    resourceFolders,
    onTemplateChange,
    onCreate,
    showManageTemplatesLink = false,
}: TeamTemplatesPaneContentProps) {
    const [selectedTag, setSelectedTag] = useState(TAG_FILTER_ALL);
    const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);

    const handleSelectTeamTemplate = (templateId: string) => {
        trackActivity("Presentation", "Start", null, null, {
            source: "team-template",
            template_id: templateId,
        }, { audit: false });

        onCreate({ type: "team-template", templateId });
    };

    const handleSelectStarterTemplates = () => {
        if (onTemplateChange) {
            onTemplateChange("starter");
            return;
        }

        AppController.showCreatePresentationDialog({ pane: CreatePresentationDialogPaneType.STARTER_TEMPLATES });
    };

    const handleSearch = (query: string) => {
        PresentationTemplatesPaneController.setSearch({
            query: query?.trim() ?? "",
            fields: search.fields,
        });
    };

    const handleSort = (nextSort: SearchBarSort) => {
        PresentationTemplatesPaneController.setSort([{
            field: nextSort.field,
            direction: nextSort.direction,
        }]);
    };

    const handleContentFilterChange = (event: { target: { value: string } }) => {
        const searchField = event.target.value;
        PresentationTemplatesPaneController.setSearch({
            query: search.query,
            fields: searchField === SEARCH_FIELD_ALL ? undefined : [searchField],
        });
    };

    const handleTagFilterChange = (tag: string) => {
        setSelectedTag(tag);
    };

    const handleTagFilterDropdownChange = (event: { target: { value: string } }) => {
        handleTagFilterChange(event.target.value);
    };

    const handleSelectFolder = (folderId: string | null) => {
        setSelectedFolderId(folderId);
        if (search.query) {
            PresentationTemplatesPaneController.setSearch({
                query: "",
                fields: search.fields,
            });
        }
    };

    const handleSelectTeamTemplates = () => {
        setSelectedFolderId(null);
        setSelectedTag(TAG_FILTER_ALL);
    };

    const teamTagFilterOptions = [
        { field: TAG_FILTER_ALL, label: t("All Tags") },
        ...tags.map(({ tag, count }) => ({
            field: tag,
            label: `${tag} (${count})`,
        })),
    ];

    const selectedContentFilter = search.fields?.[0] ?? SEARCH_FIELD_ALL;
    const sortState: SearchBarSort = {
        field: sort[0]?.field ?? "document.modifiedAt",
        direction: sort[0]?.direction ?? "desc",
    };

    const isMainTeamTemplatesView = selectedFolderId === null && selectedTag === TAG_FILTER_ALL;
    const canUseInspirationSlides = WorkspaceController.actionPermissions[ActionPermissionsObject.INSPIRATION_SLIDES].use;
    const canUseSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use;
    const canUpgradeToUseSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(
        ActionPermissionsObject.SHARED_RESOURCES,
        PermissionActionType.USE
    );
    const shouldRenderTemplatesListHeader = WorkspaceController.isMultiUserWorkspace && !canUpgradeToUseSharedResources;

    const breadcrumb = useMemo(() => buildResourcePaneBreadcrumbData({
        rootLabel: t("Team Templates"),
        selectedFolderId,
        resourceFolders,
        onSelectFolder: handleSelectFolder,
    }), [selectedFolderId, resourceFolders, handleSelectFolder]);

    return (
        <FlexBox fill vertical top>
            <Layout>
                <SearchAndAdd
                    searchValue={search.query ?? ""}
                    onSearch={handleSearch}
                    searchPlaceholder={t("Search team templates...")}
                    contentFilterOptions={contentFilterOptions}
                    selectedContentFilter={selectedContentFilter}
                    onContentFilterChange={handleContentFilterChange}
                    tagFilterOptions={teamTagFilterOptions}
                    selectedTagFilter={selectedTag}
                    onTagFilterChange={handleTagFilterDropdownChange}
                />
                <PaneBody>
                    <SideBar
                        readOnly
                        resourceTypes={[WorkspaceResourceType.PRESENTATION_TEMPLATE]}
                        folderType={WorkspaceResourceFolderType.SHARED}
                        resourceFolders={resourceFolders}
                        selectedFolderId={selectedFolderId}
                        onSelectFolder={canUseSharedResources ? handleSelectFolder : undefined}
                    >
                        <SideBarItem
                            className={selectedClass(isMainTeamTemplatesView)}
                            onClick={handleSelectTeamTemplates}
                            data-test-id="library-templates-nav-team-templates"
                        >
                            {canUpgradeToUseSharedResources ? <UpgradeIconWrapper onClick={() => ShowUpgradeDialog({ type: UpgradePlanDialogType.UPGRADE_PLAN_TO_TEAM,
                                workspaceId: AppController.workspaceId,
                                analytics: { source: "team-templates-pane" } })} />
                                : <Icon className="material-symbols-outlined">domain</Icon>}
                            {t("Team Templates")}
                        </SideBarItem>
                        {canUseInspirationSlides && (
                            <SideBarItem
                                onClick={handleSelectStarterTemplates}
                                data-test-id="library-templates-nav-starter-templates"
                            >
                                <Icon className="material-symbols-outlined">lightbulb</Icon>
                                {t("Beautiful.ai Templates")}
                            </SideBarItem>
                        )}
                    </SideBar>
                    <PaneContent>
                        {shouldRenderTemplatesListHeader && (
                            <TemplatesListHeaderRow>
                                <PresentationLibraryPaneBreadcrumb {...breadcrumb} />
                                <TemplatesListHeaderActions>
                                    <ManageTemplatesLink visible={showManageTemplatesLink} />
                                    <PresentationListHeader
                                        sortOptions={sortOptions}
                                        sort={sortState}
                                        searchQuery={search.query ?? ""}
                                        onSetSort={handleSort}
                                        showViewTypeToggle={false}
                                    />
                                </TemplatesListHeaderActions>
                            </TemplatesListHeaderRow>
                        )}
                        <TemplatesContent>
                            <PresentationTemplatesPane
                                editable={false}
                                embedded
                                folderId={selectedFolderId ?? undefined}
                                tagFilter={selectedTag}
                                onTagFilterChange={handleTagFilterChange}
                                onSelect={(presentationId, isDoubleClick) => {
                                    if (isDoubleClick && presentationId) {
                                        handleSelectTeamTemplate(presentationId);
                                    }
                                }}
                            />
                        </TemplatesContent>
                    </PaneContent>
                </PaneBody>
            </Layout>
        </FlexBox>
    );
}

export const TeamTemplatesPane = PresentationTemplatesPaneController.withState(
    WorkspaceController.withState(TeamTemplatesPaneContent)
);
